import { cn } from '@/lib/utils';
import {
    ChevronLeft,
    ChevronRight,
    Download,
    ExternalLink,
    X
} from 'lucide-react';
import { useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';

import { Button } from '@/components/ui/button';

interface ArtifactFullViewProps {
    title: string;
    subtitle?: string;
    html: string;
    frameKey: string;
    sandbox: string;
    count: number;
    activeIndex: number;
    container?: HTMLElement | null;
    onSelect: (index: number) => void;
    onClose: () => void;
    onDownload: () => void;
    onOpenInNewTab: () => void;
}

export function ArtifactFullView({
    title,
    subtitle,
    html,
    frameKey,
    sandbox,
    count,
    activeIndex,
    container,
    onSelect,
    onClose,
    onDownload,
    onOpenInNewTab
}: ArtifactFullViewProps) {
    const hasMultiple = count > 1;

    const showRelative = useCallback(
        (offset: number) => onSelect((activeIndex + offset + count) % count),
        [activeIndex, count, onSelect]
    );

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
            if (!hasMultiple) return;
            if (e.key === 'ArrowLeft') showRelative(-1);
            if (e.key === 'ArrowRight') showRelative(1);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [hasMultiple, onClose, showRelative]);

    return createPortal(
        <div
            className={cn(
                'z-50 inset-0 bg-black/40 backdrop-blur-[2px] animate-in fade-in duration-150',
                container ? 'absolute' : 'fixed'
            )}
            onClick={onClose}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-label={title}
                onClick={(e) => e.stopPropagation()}
                className="absolute inset-3 md:inset-8 flex flex-col bg-background shadow-2xl border rounded-xl overflow-hidden"
            >
                <div className="items-center gap-2 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] px-4 py-2 border-b">
                    <div className="min-w-0">
                        <p className="font-medium truncate">{title}</p>
                        {subtitle && (
                            <p className="text-muted-foreground text-xs truncate">
                                {subtitle}
                            </p>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        {hasMultiple && (
                            <>
                                <Button
                                    size="icon-sm"
                                    variant="ghost"
                                   
                                    onClick={() => showRelative(-1)}
                                    aria-label="Previous artifact"
                                >
                                    <ChevronLeft />
                                </Button>
                                <span className="text-muted-foreground text-sm tabular-nums">
                                    {activeIndex + 1} / {count}
                                </span>
                                <Button
                                    size="icon-sm"
                                    variant="ghost"
                                   
                                    onClick={() => showRelative(1)}
                                    aria-label="Next artifact"
                                >
                                    <ChevronRight />
                                </Button>
                            </>
                        )}
                    </div>
                    <div className="flex justify-end items-center gap-1">
                        <Button
                            size="icon"
                            variant="ghost"
                            onClick={onOpenInNewTab}
                            aria-label="Open in new tab"
                        >
                            <ExternalLink />
                        </Button>
                        <Button
                            size="icon"
                            variant="ghost"
                            onClick={onDownload}
                            aria-label="Download"
                        >
                            <Download />
                        </Button>
                        <Button
                            size="icon"
                            variant="ghost"
                            onClick={onClose}
                            aria-label="Close"
                        >
                            <X />
                        </Button>
                    </div>
                </div>
                <iframe
                    key={frameKey}
                    title={title}
                    srcDoc={html}
                    sandbox={sandbox}
                    className="flex-1 bg-white border-0 w-full"
                />
            </div>
        </div>,
        container ?? document.body
    );
}
